import { useState, useEffect, useCallback, useRef } from 'react'
import req from '@sportninja/common/api/request'
import { sortFacilityTimeSlots } from '../utils/facilityTimeSlotOrder'

/**
 * Days of the week (0 = Sunday, 6 = Saturday)
 */
export const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
] as const

/**
 * Recurrence types
 */
export const RECURRENCE_TYPES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'specific', label: 'Specific Date' },
] as const

/**
 * Time range structure
 */
export interface TimeRange {
  id: string
  start_time: string
  end_time: string
  sort_order: number
  duration_minutes?: number
  formatted_range?: string
}

/**
 * Time slot structure from API
 */
export interface TimeSlot {
  id: string
  label: string
  recurrence_type: 'weekly' | 'biweekly' | 'monthly' | 'specific'
  slot_date?: string | null // "YYYY-MM-DD" for imported/date-specific slots
  is_active: boolean
  is_imported?: boolean
  days: number[]
  time_ranges: TimeRange[]
  facility_id: string
  schedule_id: string
  divisions?: string[]
  created_at?: string
  updated_at?: string
}

/**
 * Time slot count per facility
 */
export interface TimeSlotCount {
  facility_id: string
  count: number
}

/**
 * Data for creating a new time slot
 */
export interface CreateTimeSlotData {
  label: string
  recurrence_type: 'weekly' | 'biweekly' | 'monthly' | 'specific'
  days: number[]
  time_ranges: { start_time: string; end_time: string }[]
  divisions?: string[]
  slot_date?: string | null
}

/**
 * Data for updating a time slot
 */
export interface UpdateTimeSlotData {
  label?: string
  recurrence_type?: 'weekly' | 'biweekly' | 'monthly' | 'specific'
  days?: number[]
  time_ranges?: { id?: string; start_time: string; end_time: string }[]
  is_active?: boolean
  divisions?: string[]
  slot_date?: string | null
}

/**
 * Returns a fingerprint string for a time slot, used to group identical
 * slots across facilities into a single displayed row.
 */
export const getSlotFingerprint = (slot: TimeSlot): string => {
  const days = [...(slot.days || [])].sort((a, b) => a - b).join(',')
  const tr = slot.time_ranges?.[0]
  const start = tr?.start_time?.slice(0, 5) || ''
  const end = tr?.end_time?.slice(0, 5) || ''
  const divs = [...(slot.divisions || [])].sort().join(',')
  const dateKey = slot.slot_date || ''
  return `${slot.recurrence_type}|${days}|${start}|${end}|${divs}|${dateKey}`
}

/**
 * Hook result interface
 */
interface UseSchedulerTimeSlotsResult {
  /** List of time slots for the schedule */
  timeSlots: TimeSlot[]
  /** Time slot counts per facility */
  timeSlotCounts: Record<string, number>
  /** Loading state */
  loading: boolean
  /** Loading state for counts */
  loadingCounts: boolean
  /** Error state */
  error: Error | null
  /** Refetch time slots AND the per-facility counts */
  refetch: () => Promise<void>
  /** Fetch time slot counts */
  fetchCounts: () => Promise<void>
  /** Create a new time slot */
  createTimeSlot: (
    facilityId: string,
    data: CreateTimeSlotData
  ) => Promise<TimeSlot>
  /** Update a time slot */
  updateTimeSlot: (
    timeSlotId: string,
    data: UpdateTimeSlotData
  ) => Promise<TimeSlot>
  /** Delete a time slot */
  deleteTimeSlot: (timeSlotId: string) => Promise<void>
  /** Duplicate a time slot */
  duplicateTimeSlot: (
    timeSlotId: string,
    targetFacilityId?: string
  ) => Promise<TimeSlot>
  /** Get time slots for a specific facility */
  getTimeSlotsForFacility: (facilityId: string) => TimeSlot[]
  /** Create a time slot for every facility in a venue (fan-out) */
  createTimeSlotForVenue: (
    facilityIds: string[],
    data: CreateTimeSlotData
  ) => Promise<void>
  /** Update multiple time slots in parallel */
  updateTimeSlotBatch: (
    timeSlotIds: string[],
    data: UpdateTimeSlotData
  ) => Promise<void>
  /** Delete multiple time slots in parallel */
  deleteTimeSlotBatch: (timeSlotIds: string[]) => Promise<void>
  /** Delete all imported time slots */
  deleteAllImported: () => Promise<void>
  /** Delete all time slots for the current flight */
  deleteAllForFlight: () => Promise<void>
}

/**
 * Custom hook for managing scheduler time slots
 * @param competitionId - The competition/schedule ID to manage time slots for
 * @returns Object containing time slots data, loading states, and CRUD functions
 */
export const useSchedulerTimeSlots = (
  competitionId?: string,
  flightId?: string | null
): UseSchedulerTimeSlotsResult => {
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([])
  const [timeSlotCounts, setTimeSlotCounts] = useState<Record<string, number>>(
    {}
  )
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingCounts, setLoadingCounts] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  // Local divisions mapping - preserved across API refetches
  // since the API may not yet return divisions in the response
  const divisionsMapRef = useRef<Record<string, string[]>>({})

  // Latest-request-wins generation counters. SEPARATE counters per slice on
  // purpose: the mount effect fires both fetches concurrently, so a single
  // shared counter would let the counts request invalidate the list response
  // and leave `timeSlots` empty forever.
  const timeSlotsRequestIdRef = useRef(0)
  const countsRequestIdRef = useRef(0)

  // The (competition, flight) pair currently on screen, synced during RENDER.
  //
  // A request id only encodes START ORDER, which is not enough on its own: the
  // CRUD helpers capture `fetchTimeSlots`/`fetchCounts` closures bound to the
  // flight that was selected when the mutation began. If the user switches
  // flights while a save is in flight, that stale closure runs AFTERWARDS and
  // allocates the NEWEST request id — so an id-only guard would happily let
  // Flight A's rows overwrite the Flight B list the user is now looking at
  // (and the switcher stays enabled while a surface is saving, so this is
  // reachable from the real UI). Every fetch therefore also checks that its own
  // scope is still the current one.
  const scopeKey = `${competitionId ?? ''}|${flightId ?? ''}`
  const scopeKeyRef = useRef(scopeKey)
  scopeKeyRef.current = scopeKey

  /**
   * Fetches all time slots for the schedule
   */
  const fetchTimeSlots = useCallback(async () => {
    // The scope this call belongs to, derived from the same values the closure
    // was built with. A mutation continuation from an old flight fails here and
    // never even issues its GET.
    const scope = `${competitionId ?? ''}|${flightId ?? ''}`
    if (scopeKeyRef.current !== scope) {
      return
    }

    // Allocate the generation BEFORE the early return, so a direct refetch()
    // with an empty competitionId also invalidates work already in flight.
    const requestId = ++timeSlotsRequestIdRef.current

    if (!competitionId) {
      setTimeSlots([])
      setLoading(false)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const qs = flightId ? `?flight_id=${flightId}` : ''
      const { data } = await req(`/schedules/${competitionId}/time-slots${qs}`)
      // Scope re-checked after every await: it can change mid-request too.
      if (
        timeSlotsRequestIdRef.current !== requestId ||
        scopeKeyRef.current !== scope
      ) {
        return
      }
      const slots: TimeSlot[] = data || []
      // Merge locally stored divisions back into refetched data
      const mergedSlots = slots.map((slot) => ({
        ...slot,
        divisions:
          slot.divisions || divisionsMapRef.current[slot.id] || undefined,
      }))
      setTimeSlots(mergedSlots)
    } catch (err: unknown) {
      if (
        timeSlotsRequestIdRef.current !== requestId ||
        scopeKeyRef.current !== scope
      ) {
        return
      }
      setError(
        err instanceof Error
          ? err
          : new Error('An error occurred while fetching time slots')
      )
      setTimeSlots([])
    } finally {
      if (
        timeSlotsRequestIdRef.current === requestId &&
        scopeKeyRef.current === scope
      ) {
        setLoading(false)
      }
    }
  }, [competitionId, flightId])

  /**
   * Fetches time slot counts per facility
   */
  const fetchCounts = useCallback(async () => {
    // Same scope gate as fetchTimeSlots — see the scopeKeyRef comment above.
    const scope = `${competitionId ?? ''}|${flightId ?? ''}`
    if (scopeKeyRef.current !== scope) {
      return
    }

    const requestId = ++countsRequestIdRef.current

    if (!competitionId) {
      setTimeSlotCounts({})
      setLoadingCounts(false)
      return
    }

    setLoadingCounts(true)

    try {
      const qs = flightId ? `?flight_id=${flightId}` : ''
      const { data } = await req(
        `/schedules/${competitionId}/time-slots/counts${qs}`
      )
      if (
        countsRequestIdRef.current !== requestId ||
        scopeKeyRef.current !== scope
      ) {
        return
      }
      const counts: Record<string, number> = {}
      if (Array.isArray(data)) {
        data.forEach((item: TimeSlotCount) => {
          if (item.facility_id) {
            counts[item.facility_id] = item.count
          }
        })
      }
      setTimeSlotCounts(counts)
    } catch (err: unknown) {
      if (
        countsRequestIdRef.current !== requestId ||
        scopeKeyRef.current !== scope
      ) {
        return
      }
      // Don't set error for counts - it's secondary data
      console.error('Failed to fetch time slot counts:', err)
    } finally {
      if (
        countsRequestIdRef.current === requestId &&
        scopeKeyRef.current === scope
      ) {
        setLoadingCounts(false)
      }
    }
  }, [competitionId, flightId])

  /**
   * Refetches both the list and the per-facility counts.
   *
   * The counts drive the "N Time Slots" surface header, which short-circuits on
   * a stale non-zero count — so a list-only refresh leaves the header wrong
   * after an import.
   */
  const refetchAll = useCallback(async (): Promise<void> => {
    await fetchTimeSlots()
    await fetchCounts()
  }, [fetchTimeSlots, fetchCounts])

  /**
   * Creates a new time slot for a facility
   */
  const createTimeSlot = useCallback(
    async (facilityId: string, data: CreateTimeSlotData): Promise<TimeSlot> => {
      if (!competitionId) {
        throw new Error('Competition ID is required')
      }

      const bodyData = flightId ? { ...data, flight_id: flightId } : data
      const response = await req(
        `/schedules/${competitionId}/facilities/${facilityId}/time-slots`,
        {
          method: 'POST',
          body: JSON.stringify(bodyData),
        }
      )

      // Store divisions locally if provided
      if (data.divisions && data.divisions.length > 0 && response.data?.id) {
        divisionsMapRef.current[response.data.id] = data.divisions
      }

      // Refetch to update the list
      await fetchTimeSlots()
      await fetchCounts()

      return response.data
    },
    [competitionId, flightId, fetchTimeSlots, fetchCounts]
  )

  /**
   * Updates an existing time slot
   */
  const updateTimeSlot = useCallback(
    async (timeSlotId: string, data: UpdateTimeSlotData): Promise<TimeSlot> => {
      if (!competitionId) {
        throw new Error('Competition ID is required')
      }

      const response = await req(
        `/schedules/${competitionId}/time-slots/${timeSlotId}`,
        {
          method: 'PUT',
          body: JSON.stringify(data),
        }
      )

      // Store divisions locally
      if (data.divisions && data.divisions.length > 0) {
        divisionsMapRef.current[timeSlotId] = data.divisions
      } else if (data.divisions !== undefined) {
        // Explicitly cleared divisions
        delete divisionsMapRef.current[timeSlotId]
      }

      // Refetch to update the list
      await fetchTimeSlots()
      await fetchCounts()

      return response.data
    },
    [competitionId, fetchTimeSlots, fetchCounts]
  )

  /**
   * Deletes a time slot
   */
  const deleteTimeSlot = useCallback(
    async (timeSlotId: string): Promise<void> => {
      if (!competitionId) {
        throw new Error('Competition ID is required')
      }

      await req(`/schedules/${competitionId}/time-slots/${timeSlotId}`, {
        method: 'DELETE',
      })

      // Clean up local divisions data
      delete divisionsMapRef.current[timeSlotId]

      // Refetch to update the list
      await fetchTimeSlots()
      await fetchCounts()
    },
    [competitionId, fetchTimeSlots, fetchCounts]
  )

  /**
   * Duplicates a time slot, optionally to a different facility
   */
  const duplicateTimeSlot = useCallback(
    async (
      timeSlotId: string,
      targetFacilityId?: string
    ): Promise<TimeSlot> => {
      if (!competitionId) {
        throw new Error('Competition ID is required')
      }

      const body = targetFacilityId
        ? JSON.stringify({ facility_id: targetFacilityId })
        : undefined

      const response = await req(
        `/schedules/${competitionId}/time-slots/${timeSlotId}/duplicate`,
        {
          method: 'POST',
          body,
        }
      )

      // Refetch to update the list
      await fetchTimeSlots()
      await fetchCounts()

      return response.data
    },
    [competitionId, fetchTimeSlots, fetchCounts]
  )

  /**
   * Creates a time slot for every facility in a venue (fan-out).
   * Calls POST per facility sequentially to avoid database deadlocks,
   * then refetches once.
   */
  const createTimeSlotForVenue = useCallback(
    async (facilityIds: string[], data: CreateTimeSlotData): Promise<void> => {
      if (!competitionId) {
        throw new Error('Competition ID is required')
      }

      const bodyData = flightId ? { ...data, flight_id: flightId } : data
      const failed: string[] = []
      for (const fid of facilityIds) {
        try {
          const response = await req(
            `/schedules/${competitionId}/facilities/${fid}/time-slots`,
            {
              method: 'POST',
              body: JSON.stringify(bodyData),
            }
          )
          if (
            data.divisions &&
            data.divisions.length > 0 &&
            response.data?.id
          ) {
            divisionsMapRef.current[response.data.id] = data.divisions
          }
        } catch {
          failed.push(fid)
        }
      }

      if (failed.length === facilityIds.length) {
        throw new Error('Failed to create time slot for all facilities')
      }

      await fetchTimeSlots()
      await fetchCounts()

      if (failed.length > 0) {
        throw new Error(
          `Failed to create time slot for ${failed.length} of ${facilityIds.length} surfaces`
        )
      }
    },
    [competitionId, flightId, fetchTimeSlots, fetchCounts]
  )

  /**
   * Updates multiple time slots in parallel, then refetches once.
   */
  const updateTimeSlotBatch = useCallback(
    async (timeSlotIds: string[], data: UpdateTimeSlotData): Promise<void> => {
      if (!competitionId) {
        throw new Error('Competition ID is required')
      }

      const results = await Promise.allSettled(
        timeSlotIds.map(async (tsId) => {
          const response = await req(
            `/schedules/${competitionId}/time-slots/${tsId}`,
            {
              method: 'PUT',
              body: JSON.stringify(data),
            }
          )
          if (data.divisions && data.divisions.length > 0) {
            divisionsMapRef.current[tsId] = data.divisions
          } else if (data.divisions !== undefined) {
            delete divisionsMapRef.current[tsId]
          }
          return response
        })
      )

      const failures = results.filter((r) => r.status === 'rejected')
      if (failures.length === timeSlotIds.length) {
        throw new Error('Failed to update all time slots')
      }

      await fetchTimeSlots()
      await fetchCounts()
    },
    [competitionId, fetchTimeSlots, fetchCounts]
  )

  /**
   * Deletes multiple time slots in parallel, then refetches once.
   */
  const deleteTimeSlotBatch = useCallback(
    async (timeSlotIds: string[]): Promise<void> => {
      if (!competitionId) {
        throw new Error('Competition ID is required')
      }

      const results = await Promise.allSettled(
        timeSlotIds.map(async (tsId) => {
          await req(`/schedules/${competitionId}/time-slots/${tsId}`, {
            method: 'DELETE',
          })
          delete divisionsMapRef.current[tsId]
        })
      )

      const failures = results.filter((r) => r.status === 'rejected')
      if (failures.length === timeSlotIds.length) {
        throw new Error('Failed to delete all time slots')
      }

      await fetchTimeSlots()
      await fetchCounts()
    },
    [competitionId, fetchTimeSlots, fetchCounts]
  )

  /**
   * Deletes all imported time slots for the schedule
   */
  const deleteAllImported = useCallback(async (): Promise<void> => {
    if (!competitionId) {
      throw new Error('Competition ID is required')
    }

    await req(`/schedules/${competitionId}/time-slots/imported`, {
      method: 'DELETE',
    })

    await fetchTimeSlots()
    await fetchCounts()
  }, [competitionId, fetchTimeSlots, fetchCounts])

  /**
   * Deletes all time slots for the current flight
   */
  const deleteAllForFlight = useCallback(async (): Promise<void> => {
    if (!competitionId || !flightId) {
      throw new Error('Competition ID and flight ID are required')
    }

    await req(`/schedules/${competitionId}/time-slots/flight/${flightId}`, {
      method: 'DELETE',
    })

    await fetchTimeSlots()
    await fetchCounts()
  }, [competitionId, flightId, fetchTimeSlots, fetchCounts])

  /**
   * Gets time slots filtered by facility ID
   */
  const getTimeSlotsForFacility = useCallback(
    (facilityId: string): TimeSlot[] => {
      return sortFacilityTimeSlots(
        timeSlots.filter((slot) => slot.facility_id === facilityId)
      )
    },
    [timeSlots]
  )

  // Clear stale data immediately when flightId changes, then refetch
  useEffect(() => {
    // Invalidate any in-flight work BEFORE the guard: when competitionId goes
    // falsy this effect clears both slices and then skips both fetches, so the
    // counters inside them never fire and an older response would land on top
    // of the deliberate clear.
    timeSlotsRequestIdRef.current++
    countsRequestIdRef.current++
    setTimeSlots([])
    setTimeSlotCounts({})
    if (competitionId) {
      fetchTimeSlots()
      fetchCounts()
    } else {
      setLoading(false)
      setLoadingCounts(false)
      setError(null)
    }
  }, [competitionId, flightId, fetchTimeSlots, fetchCounts])

  return {
    timeSlots,
    timeSlotCounts,
    loading,
    loadingCounts,
    error,
    refetch: refetchAll,
    fetchCounts,
    createTimeSlot,
    updateTimeSlot,
    deleteTimeSlot,
    duplicateTimeSlot,
    getTimeSlotsForFacility,
    createTimeSlotForVenue,
    updateTimeSlotBatch,
    deleteTimeSlotBatch,
    deleteAllImported,
    deleteAllForFlight,
  }
}

export default useSchedulerTimeSlots
